import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/layout/Navbar";
import {
  Alert, Button, DanyComposer, DanyStatus, DanyTurn, ErrorBoundary, IconButton,
  Skeleton, SuggestionChips, useViewport,
} from "@/ds";
import { DanyAvatar } from "@/ds/dany/DanyAvatar";
import DanyWidget from "@/components/dany/DanyWidgets";
import DanyPhotoStrip from "@/components/dany/DanyPhotoStrip";
import { uploadDanyPhoto } from "@/lib/danyPhotos";
import { useUploadLimitMb } from "@/lib/limits";
import { useDanyAvatarState } from "@/components/dany/useDanyAvatarState";
import { SETTLE_DELAYS, useVoiceMode } from "@/components/dany/useVoiceMode";
import useVisualViewport from "@/hooks/useVisualViewport";
import VerifyEmailBanner from "@/components/auth/VerifyEmailBanner";
import { useAuth } from "@/contexts/AuthContext";
import { listThreads, loadThread, streamChat } from "@/lib/dany";
import { TURNSTILE_SITE_KEY, loadTurnstileScript } from "@/lib/turnstile";
import { modeForPath } from "@/lib/danyPageMode";
import { useHideTabBar } from "@/hooks/useHideTabBar";
import { pathOf, withReturnTo } from "@/lib/returnTo";
import { formatDate, formatRelative } from "@/lib/dates";

/**
 * Dany's conversational surface, full screen.
 *
 * One surface, two doors: the home hero's composer and the global FAB both land
 * here, and there is no alternate panel anywhere in the app.
 *
 * The layout follows the design system's two hard rules for this screen
 * (DanyTurn.prompt.md, ui_kits/dany/README.md):
 *
 *   - **Dany is unboxed.** The assistant's words are body copy on the page; only
 *     the client's turns get a container. That single decision is what keeps
 *     this from reading as a generic chatbot.
 *   - **Turns breathe.** `--dany-gap-turn` is 32px, not 8px. It reads as a
 *     letter, not a messenger.
 *
 * The four SSE states get four distinct treatments, never one spinner — see the
 * `status` note in the component.
 */

/**
 * Tools that return a SET OF THINGS, as opposed to knowledge Dany writes from.
 *
 * A turn carrying one of these already shows its result as cards or chips, so a
 * numbered list in the same turn is not a process — it is the widget's content
 * typed out again, which the prompt (v1.2.2) forbids and still occasionally
 * does anyway, most reliably with the three plans. Steps are switched off for
 * that turn: the markers are stripped and it renders as prose, so a leak reads
 * as a redundant sentence instead of as three gold-numbered "steps" that are
 * not steps. `search_knowledge` is not here — a process answer is exactly what
 * it grounds, and those turns must keep their steps.
 */
const SET_TOOLS = new Set(["get_plans", "browse_catalog", "find_fixybees", "my_jobs", "my_quotes"]);

const PLAN_NAME = /Fixy\s?(Starter|Pro|Master)/gi;

/**
 * The plans, said once (AUDIT 17.15 residual).
 *
 * Told four different ways across prompt 1.1.0 → 1.2.2 not to restate what the
 * interface is showing, the model keeps reciting the three plans with their
 * prices directly above the three cards carrying the same prices. The steps
 * guard settled the pattern for this class of problem: when the prompt will not
 * hold a rule the renderer can decide with certainty, the renderer decides.
 *
 * Certainty is the whole reason this is narrow. It only fires when the text
 * names TWO OR MORE plans, which is a recitation and nothing else. "FixyPro is
 * $12 a month" names one, is a direct answer to a direct question, and is left
 * exactly as written — a blanket truncation would have destroyed it.
 *
 * Line-based rather than a first-sentence cut, so the closing offer survives
 * with the opening: what gets dropped is the enumeration itself.
 */
function trimRecitedPlans(text, fallback) {
  const source = String(text || "").trim();
  const named = new Set((source.match(PLAN_NAME) || []).map((s) => s.toLowerCase().replace(/\s/g, "")));
  if (named.size < 2) return source;

  const kept = source
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    // A line that names a plan and carries a number is the card, typed out —
    // and so is any line quoting an amount, which is how the extra-credit price
    // kept surviving the first cut while the cards were already showing it.
    .filter((line) => !(new RegExp(PLAN_NAME.source, "i").test(line) && /\d/.test(line)))
    .filter((line) => !/[$€£]\s?\d/.test(line))
    .slice(0, 2);

  return kept.length ? kept.join("\n") : fallback;
}

/** Rebuild the visible turns from a stored transcript. */

/**
 * A draft has ONE card, not one per turn (Jorge, production 2026-08-19).
 *
 * Widgets were appended unconditionally in both places that build them — the
 * transcript rebuild below and the live `tool_result` handler — so a client who
 * corrected the budget twice ended up looking at three draft cards at once,
 * each holding its own independent React state, each with a live Publish
 * button. Only the last one's `action_id` still worked; the other two were
 * loaded guns pointed at proposals the server had already superseded.
 *
 * The server has always had one live proposal per thread: a second
 * `propose_job` replaces the anonymous draft row and mints a fresh action id.
 * This makes the UI say the same thing. `publish_job` supersedes the draft it
 * came from as well — once a job is live, the card offering to publish it is
 * not a stale view, it is a wrong one.
 */
const SUPERSEDES = {
  propose_job: ["propose_job"],
  publish_job: ["propose_job", "publish_job"],
  // N9 (e). A discard settles the same draft a publish does, so it replaces
  // the same cards — otherwise the "it is gone" card and a live Publish button
  // would sit on screen together, and only one of them would be true.
  discard_job_draft: ["propose_job", "publish_job", "discard_job_draft"],
  request_quote: ["request_quote"],
  update_profile: ["update_profile"],
  // T11 N47. **The table stopped at DY2.1's client tools and the FixyBee's
  // were never added**, so the rule that keeps one live proposal on screen
  // held for a job post and not for a quote — the channel where it costs
  // money. Measured in production, thread dthr_c8b2d5779126: three
  // `send_quote` proposals alive at once, `line_items: []`, then $500, then
  // $600, each with its own card and its own Send button, all still
  // `proposed` and none of them expired. The FixyBee dictated a correction,
  // Dany recorded it perfectly, and the card in front of them kept the old
  // figure — "los renglones dictados no llegan a la tarjeta".
  //
  // `propose` does not reject its predecessors and `mark_executed` lets the
  // browser's payload win, so the stale card is not a dead button: it is a
  // live one that sends what nobody dictated and spends a credit doing it.
  // That half is closed server-side in its own commit; this half is what the
  // person sees.
  propose_quote: ["propose_quote"],
  send_quote: ["propose_quote", "send_quote"],
  // The offer and its receipt are the same card in two states, which is the
  // argument `update_profile` already makes here.
  provider_setup: ["provider_setup"],
  open_ticket: ["open_ticket"],
  // Here because the server now agrees with it: a new proposal retires the
  // one it replaces (T11 N47b), so a card left on screen for a superseded
  // offer would be a Save button that answers 409.
  add_job_photos: ["add_job_photos"],
};

/** Append `widget`, dropping whatever it replaces, across every turn. */
function addWidget(turns, turnIndex, widget) {
  const replaces = SUPERSEDES[widget.tool];
  const next = replaces
    ? turns.map((turn) => ({
        ...turn,
        widgets: (turn.widgets || []).filter((w) => !replaces.includes(w.tool)),
      }))
    : turns.map((turn) => ({ ...turn, widgets: [...(turn.widgets || [])] }));
  next[turnIndex] = {
    ...next[turnIndex],
    widgets: [...(next[turnIndex].widgets || []), widget],
  };
  return next;
}

/**
 * Apply the same supersession across a whole list of turns, wherever they came
 * from.
 *
 * `addWidget` and `turnsFromTranscript` each enforce this for their own
 * channel, and a voice call went through neither: `useVoiceMode` appends the
 * cards a call produces as they are fetched, with no notion of what they
 * replace. So a caller who corrected the draft twice — which is the normal
 * shape of dictating a job out loud, one field per turn — ended up looking at
 * three Publish buttons, of which only the last one's `action_id` was still
 * live. Production, dthr_f890ba159d66: three `propose_job` proposals at
 * 03:06:50, 03:07:30 and 03:07:49, and the caller told Dany *"I clicked on the
 * Publish this job button, but it just won't publish anything."*
 *
 * Doing it here, over the merged list, is what makes the rule one rule: a card
 * a call produces supersedes one already in the stored transcript, and the
 * reload that used to be the only way to see a single card is no longer the
 * difference.
 *
 * A turn emptied by this — a voice tool turn whose only card was replaced —
 * goes with it. A turn that was already empty is left alone: that is the
 * placeholder a streaming text answer is being written into, and removing it
 * would take the status indicator's home with it.
 */
function dropSuperseded(turns) {
  const flat = [];
  turns.forEach((turn, ti) => (turn.widgets || []).forEach(
    (widget, wi) => flat.push({ ti, wi, tool: widget.tool, seq: widget.seq })
  ));
  // **Dominance is decided in thread order, which is not list order** (T11
  // N40). The screen renders the stored conversation and then what is being
  // said out loud, and during a call those two are not chronological: a
  // receipt written by `POST /dany/actions/{id}/execute` lands in the stored
  // transcript while the proposals it settles are still live turns further
  // down the array. Read in list order the stale proposal came last and
  // killed the receipt, so pressing Publish on a call left the draft card on
  // screen offering to publish a job that was already published. `seq` is the
  // server's own ordering and both sides now carry it; entries without one
  // keep their position, so a locally-made widget that never had a row
  // behaves as it did.
  const order = flat.map((entry, i) => i).sort((a, b) => {
    const sa = flat[a].seq;
    const sb = flat[b].seq;
    if (typeof sa === "number" && typeof sb === "number") return sa - sb || a - b;
    return a - b;
  });
  const dead = new Set();
  order.forEach((index, i) => {
    const entry = flat[index];
    const replaces = SUPERSEDES[entry.tool];
    if (!replaces) return;
    for (let j = 0; j < i; j += 1) {
      const earlier = flat[order[j]];
      if (replaces.includes(earlier.tool)) dead.add(`${earlier.ti}:${earlier.wi}`);
    }
  });
  if (!dead.size) return turns;
  return turns
    .map((turn, ti) => {
      if (!turn.widgets?.length) return turn;
      const widgets = turn.widgets.filter((_, wi) => !dead.has(`${ti}:${wi}`));
      return widgets.length === turn.widgets.length ? turn : { ...turn, widgets };
    })
    .filter((turn, ti) => (
      turn.widgets?.length || turn.text || !(turns[ti].widgets || []).length
    ));
}

function turnsFromTranscript(messages = []) {
  const turns = [];
  let current = null;
  for (const message of messages) {
    if (message.role === "user") {
      // T19 F4: the photos this turn carried, drawn above its words.
      turns.push({
        kind: "user", text: message.content, id: message.message_id,
        photos: (message.attachments || []).map((a) => a.url),
      });
      current = null;
      continue;
    }
    if (message.role === "tool") {
      if (!current) {
        current = { kind: "dany", text: "", widgets: [], id: message.message_id };
        turns.push(current);
      }
      // Same supersession the live stream applies, so a reloaded conversation
      // shows the one card it showed before the refresh rather than every
      // draft the client ever corrected.
      const replaces = SUPERSEDES[message.tool_name];
      if (replaces) {
        for (const turn of turns) {
          turn.widgets = (turn.widgets || []).filter((w) => !replaces.includes(w.tool));
        }
      }
      current.widgets.push({
        tool: message.tool_name, data: message.tool_data,
        id: message.message_id, seq: message.seq,
      });
      continue;
    }
    // assistant. Rows that carried a tool call repeat the same partial text once
    // per call, so identical consecutive content is folded rather than shown
    // twice.
    const text = (message.content || "").trim();
    if (!current) {
      current = { kind: "dany", text, widgets: [], id: message.message_id };
      turns.push(current);
    } else if (text && text !== current.text) {
      current.text = current.text ? `${current.text}\n\n${text}` : text;
    }
  }
  return turns;
}

/**
 * The transcript, reconciled against the draft that actually still exists (N53).
 *
 * **The bug this closes, with the row that proves it.** Production
 * dthr_49848d73b167, 2026-09-23 17:23: the client discarded the draft from the
 * card's own button, confirmed, and the card came straight back — live Publish
 * button and all. They confirmed a second time and it went. The database says
 * the discard happened ONCE: `anonymous_drafts.updated_at` 17:23:41.595,
 * `status = 'discarded'`, and the single `publish_job` proposal `rejected` at
 * 17:23:41.614. The server did exactly what it was asked, the first time.
 *
 * What came back was the client's own picture of it. The card's knowledge that
 * the draft is gone lived in one place — the component's `stage` state — and
 * `reconcileThread` rebuilds the widget list from the transcript, where the
 * `propose_job` row still sits because a discard by BUTTON writes no message
 * (the tool writes one; `rejectAction` does not). The rebuilt widget carries a
 * different React key: live streaming ids it `propose_job-1-0`, the transcript
 * ids it by `message_id`. A changed key is a remount, a remount is a fresh
 * `stage`, and the card was on screen again with nothing left to discard.
 *
 * The second press worked for the same reason the first did not: by then the
 * card was transcript-derived, so `onCommitted` re-read it under the SAME key,
 * React kept the instance, and `stage` survived. Two presses, one discard —
 * which is what the audit row shows.
 *
 * So the client stops remembering and starts asking. `live_draft` is the
 * server's answer to "is there still a draft here", and a `propose_job` card is
 * a picture of the turn that made one. When there is no live draft the picture
 * is not history being shown, it is a live Publish button for a row that is
 * gone — so it is dropped. Null covers all three ways a draft stops being live:
 * discarded from the card, discarded by Dany, published. In the last two the
 * transcript already superseded the card and this does nothing, which is the
 * point — one rule, no case analysis.
 *
 * **It also fixes the reload.** The same phantom card was there for anyone who
 * simply reopened the conversation after discarding: the initial load rendered
 * the transcript untouched whenever `live_draft` was null.
 */
function withLiveDraft(turns, live) {
  if (live) {
    // Appended as one more `propose_job` widget on purpose: `dropSuperseded`
    // already knows a later one replaces every earlier one, so the screen ends
    // up with a single Publish button without this code counting cards.
    return dropSuperseded([...turns, {
      kind: "dany", text: "", id: `live-draft-${live.draft_id}`,
      widgets: [{ tool: "propose_job", data: live, id: `live-${live.draft_id}` }],
    }]);
  }
  return turns.map((turn) => ({
    ...turn,
    widgets: (turn.widgets || []).filter((w) => w.tool !== "propose_job"),
  }));
}

/**
 * The voice state machine, expressed as Dany's face (DY1.6-UX).
 *
 * Every value here is a state `DanyAvatar` already draws for the text channel —
 * the point of the mapping is that the same situation wears the same face on
 * both channels. `thinking` is Dany reasoning and `working` is Dany running a
 * tool, which is exactly the distinction the text surface makes; a voice call
 * running `find_fixybees` should not look different from a typed one.
 *
 * `ending` stays on `listening`: the caller has not gone anywhere yet, and the
 * countdown ring is what says time is running out. Swapping the face as well
 * would read as Dany giving up on them.
 */
const VOICE_AVATAR_STATE = {
  connecting: "thinking",
  listening: "listening",
  thinking: "thinking",
  speaking: "speaking",
  ending: "listening",
};

/**
 * Dany, present, with what she is doing said underneath.
 *
 * The ring is the whole reason this is a component rather than three lines
 * inline: it is an SVG circle whose dash offset animates from full to empty over
 * the warning window, so "you have a few seconds left" is legible without a
 * number and without a modal. It renders only in `ending`, and the moment the
 * caller makes a sound the state leaves `ending` and the ring unmounts — which
 * is the rescue, and it costs them nothing but talking.
 */
function VoicePresence({ state, tool, countdownMs, label }) {
  const size = 44;
  const radius = (size - 4) / 2;
  const circumference = 2 * Math.PI * radius;
  const avatarState = state === "thinking" && tool
    ? "working" : VOICE_AVATAR_STATE[state] || "listening";

  return (
    <div
      data-testid="dany-voice-status"
      data-voice-state={state}
      style={{
        display: "flex", alignItems: "center", gap: "var(--space-3)",
        marginBottom: "var(--space-3)",
      }}
    >
      <span style={{
        position: "relative", display: "inline-flex",
        alignItems: "center", justifyContent: "center", width: size, height: size,
      }}>
        {state === "ending" && (
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden
            data-testid="dany-voice-countdown"
            style={{ position: "absolute", inset: 0, transform: "rotate(-90deg)" }}>
            <circle cx={size / 2} cy={size / 2} r={radius} fill="none"
              stroke="var(--border-default)" strokeWidth="2" />
            <circle cx={size / 2} cy={size / 2} r={radius} fill="none"
              stroke="var(--worker)" strokeWidth="2.5" strokeLinecap="round"
              strokeDasharray={circumference}
              style={{
                animation: `fixy-voice-countdown ${countdownMs}ms linear forwards`,
                // The keyframe empties the ring by offsetting a full
                // circumference, which depends on the radius — so the radius is
                // handed to the stylesheet rather than duplicated in it.
                "--dany-countdown-circumference": `${circumference}px`,
                strokeDashoffset: 0,
              }} />
          </svg>
        )}
        <DanyAvatar size={30} state={avatarState} halo={state !== "ending"} />
      </span>
      <span style={{
        fontFamily: "var(--font-body)", fontSize: "var(--text-sm)",
        color: state === "ending" ? "var(--text-body)" : "var(--text-subtle)",
      }}>
        {label}
      </span>
    </div>
  );
}

/**
 * The backstop (N35).
 *
 * The per-widget boundaries below cover the failure that actually happened and
 * cover it well: a card that throws costs its rectangle and the conversation
 * carries on around it. This one covers the rest of the screen — the sidebar,
 * the composer, the transcript rebuild — for which there is no finer place to
 * stand. It does not preserve the UI, because at that point there is no UI left
 * to preserve; what it guarantees is the thing the white screen took away,
 * which is that the person is told something went wrong and on which screen,
 * instead of being left looking at a blank document with the URL still in the
 * bar and no way to know whether their conversation still exists.
 *
 * Keyed on the route so walking into another conversation clears it: a thread
 * that failed to render must not make the next one unopenable.
 */
export default function DanyThread() {
  const { threadId } = useParams();
  const { t } = useTranslation();
  return (
    <ErrorBoundary
      label="dany-thread"
      resetKey={threadId || "new"}
      title={t("dany.errors.shellTitle")}
    >
      <DanyThreadPage />
    </ErrorBoundary>
  );
}

function DanyThreadPage() {
  const { threadId: routeThreadId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { isDesktop } = useViewport();

  const seed = location.state?.seed;
  // The landing hero's microphone hands the intent over rather than opening a
  // call of its own — see the comment on its composer. Consumed once: a reload
  // of this URL must not redial.
  const seededVoice = location.state?.startVoice;
  const seededMode = location.state?.mode || modeForPath("/");
  // R7. The job "Cotizar con Dany" was pressed on, carried through router state
  // exactly as `seed` and `mode` are. Sent once, with the message that creates
  // the thread; from then on the server holds it on `dany_threads.about_job_id`
  // and every turn is told about it, so a reload of this URL keeps the subject
  // instead of leaving Dany to work it out from the transcript.
  const seededJobId = location.state?.jobId;
  const language = String(i18n.language || "en").toLowerCase().startsWith("es") ? "es" : "en";

  const [threadId, setThreadId] = useState(routeThreadId || null);
  const [turns, setTurns] = useState([]);
  const [value, setValue] = useState("");
  // T19 F4. Photos uploaded and waiting for Send, `{ file_id, url }` each.
  // They go out with the next turn, words or not — the image is an input of
  // the thread with the same rank as text (D3).
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [photoError, setPhotoError] = useState("");
  const uploadLimitMb = useUploadLimitMb();
  // { state, label, detail } — the four SSE states get four distinct treatments
  // (MIGRATION.md, decision 4). `retrieving` and `tool` are separate on purpose:
  // retrieval is Dany looking things up (seconds, nothing changes), a tool call
  // is Dany acting on the client's behalf and is the only state where something
  // in the world actually changes. Collapsing them into one spinner throws away
  // the only signal that tells a client whether anything is happening for them.
  const [status, setStatus] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [loadingTranscript, setLoadingTranscript] = useState(!!routeThreadId);
  const [threads, setThreads] = useState([]);
  // The conversation currently open, as the server describes it. Kept because
  // the sidebar list cannot always contain it — see `historyThreads`.
  const [openThread, setOpenThread] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // The ZIP the last search actually used, from the tool call's own arguments.
  // Fallback for `threadZip` when the user never typed one in a turn we can
  // read — Dany may have carried it over from earlier context.
  const [toolZip, setToolZip] = useState(null);

  const streamRef = useRef(null);
  const bottomRef = useRef(null);

  /**
   * Put the conversation back at its bottom.
   *
   * Two callers, and the second is the T16 M4 change. A render is the first —
   * a new turn, a tool card, a change of voice state — and it animates,
   * because the person is watching the conversation move. The keyboard is the
   * second, through `useVisualViewport`, and it does NOT animate: the shell
   * has just changed height under a finger that is about to type, and a smooth
   * scroll there is half a second of the composer sliding while they aim at it.
   *
   * Before this, re-anchoring was something only a render did. Taking focus
   * sets no state, so between the tap on the field and the first keystroke
   * nothing ran at all — which is precisely what the iPhone report described:
   * the field out of view until the first letter, which rendered, which
   * scrolled.
   */
  const anchorToBottom = useCallback((smooth = false) => {
    bottomRef.current?.scrollIntoView({
      behavior: smooth ? "smooth" : "auto", block: "end",
    });
  }, []);

  // The shell tracks the visible rectangle, not the dynamic viewport, so an
  // open keyboard shortens the page instead of hiding its bottom third — and
  // the same hook is now what puts the composer back under the thumb when it
  // does (T16 M4), and what keeps a staged card's focused field on screen
  // after it has (T20-M1).
  useVisualViewport(true, anchorToBottom);
  const seedFired = useRef(false);
  // The thread id this screen minted itself, as opposed to one it was routed
  // to. See the transcript effect: without it, the URL rewrite that happens
  // mid-stream reads the half-written transcript back over the live turn.
  const ownThreadId = useRef(null);
  // The last thing the visitor asked, so a failed turn can be tried again
  // instead of retyped. Most of the failures worth retrying — a dropped
  // stream, a lost network, a backend restarted mid-answer — persist nothing,
  // so the retry is a clean second attempt rather than a duplicate.
  const lastAsked = useRef(null);

  const refreshThreads = useCallback(() => {
    listThreads({ authenticated: !!user })
      .then(setThreads)
      .catch(() => setThreads([]));
  }, [user]);

  /**
   * A finished voice call, read back from the server (DY1.6).
   *
   * The post-call webhook writes the transcript some seconds after the caller
   * hangs up, so the screen asks for it a few times and takes the first answer
   * that actually contains something.
   *
   * **The retirement of the live turns is per turn, not per answer, and that is
   * the whole of the closing flicker.** The first version dropped all of them
   * the moment ANY stored transcript came back, guarded only by
   * `stored.length`. That guard is worth nothing on a thread that was already a
   * conversation before the call: its typed turns make `stored` non-empty from
   * the first attempt, so the 5s refetch would swap in a transcript whose
   * spoken tail the webhook had not written yet and clear the live turns that
   * were the only copy of it. The last one or two things said vanished and came
   * back at the next attempt. Now each live turn is retired only when the
   * stored transcript demonstrably contains it, so the tail stays on screen
   * until its own row lands and the conversation is continuous throughout.
   *
   * **The sidebar is refreshed here too, and that is the whole of the "the call
   * is not in my list until I reload" bug.** A voice thread is created by
   * `POST /voice/session` before the call connects, and `list_threads` filters
   * on `message_count > 0` — which is right, or every denied mic prompt would
   * leave an "Untitled" row. So the thread does not QUALIFY for the list until
   * the webhook writes the spoken turns, which happens seconds after hangup and
   * on the server, where no fetch this screen ever made would see it. The list
   * was not stale by a little; it was correct when it was taken and the thread
   * had not happened yet. Asking again at the same moment the transcript
   * settles is what closes that gap, and it is the same round trip that already
   * had to happen.
   */
  const settleVoiceTranscript = useCallback((settledThreadId, reconcileLive) => {
    if (!settledThreadId) return;
    loadThread(settledThreadId)
      .then((data) => {
        const stored = withLiveDraft(
          turnsFromTranscript(data.messages || []), data.live_draft);
        if (!stored.length) return;
        // One commit, and the live turns are retired by identity rather than
        // wholesale — React batches these two so the screen never renders a
        // frame where the stored transcript has replaced turns it does not
        // carry yet.
        setTurns(stored);
        reconcileLive?.(stored);
        // Only once there is something stored: the thread reaches the list on
        // the same write, so refreshing before that would re-fetch a list that
        // still cannot contain it.
        refreshThreads();
      })
      .catch(() => { /* try again on the next timer, or on the user's reload */ });
  }, [refreshThreads]);

  /**
   * A call minted a conversation. Take it, and put it in the address bar.
   *
   * `POST /voice/session` creates the thread before the socket opens, and this
   * screen used to hear about it only when the post-call webhook landed. In
   * between — the whole call — `threadId` stayed null, which is two failures
   * with one cause:
   *
   *   * The confirmation card looks the thread's anonymous key up by this id.
   *     Without it the Publish button sent no key, so the execute endpoint could
   *     not tell whose proposal it was and answered `action_not_found` instead
   *     of `registration_required` — and the sign-up form that code exists to
   *     open never appeared. Daniel, mid-call: *"I clicked on the Publish this
   *     job button, but it just won't publish anything or it won't prompt me
   *     for creating my account."*
   *   * A refresh landed on `/dany`, an empty composer, because the URL never
   *     named what was being said. The conversation was never lost; it was
   *     never addressed.
   *
   * `ownThreadId` is set first and on purpose: the route effect below skips its
   * fetch for a thread this screen already holds, which is what keeps the live
   * turns on screen when the URL changes underneath them.
   */
  const adoptVoiceThread = useCallback((voiceThreadId) => {
    if (!voiceThreadId) return;
    ownThreadId.current = voiceThreadId;
    setThreadId(voiceThreadId);
    // The sidebar cannot learn about this one by asking: `list_threads` counts
    // only spoken turns, and a call has none until the post-call webhook writes
    // them. Stamped now rather than left blank because now is when this
    // conversation started for this screen — a reload replaces it with the
    // server's own dates.
    setOpenThread((current) => (
      current?.thread_id === voiceThreadId ? current : {
        thread_id: voiceThreadId, title: null,
        created_at: new Date().toISOString(), last_message_at: null,
      }
    ));
    if (!routeThreadId) {
      navigate(`/dany/${voiceThreadId}`, { replace: true, state: {} });
    }
  }, [routeThreadId, navigate]);

  // Every widget the stored transcript already carries. The voice reconcile
  // reads the thread's whole widget state, so without this the cards a call
  // produced would come back a second time the moment the webhook rewrote
  // them into the transcript this screen is holding.
  const storedWidgetIds = useCallback(() => {
    const ids = new Set();
    turns.forEach((turn) => (turn.widgets || []).forEach(
      (w) => w.id && ids.add(w.id)
    ));
    return ids;
  }, [turns]);

  /* The conversation is the screen (T12-M1). It is full-height, its composer
     sits where the bar would be, and its header is the way out — so the bar
     goes for as long as this page is mounted, and comes back with the tab that
     was active before, which is simply the tab the route it returns to lights.
     A voice call asks again on top of this; see the second call below. */
  useHideTabBar();

  const {
    voiceState, voiceTool, voiceEndingMs, voiceActive, voiceError, clearVoiceError,
    liveTurns, startVoice, stopVoice, syncVoiceWidgets, attachToCall,
  } = useVoiceMode({
    threadId, language,
    onThreadReady: adoptVoiceThread,
    onTranscriptSettled: settleVoiceTranscript,
    storedWidgetIds,
  });

  /* A live call asks for the same thing the thread already asked for, and that
     is why the request is a counter rather than a flag: the call ending must
     not put the bar back while the conversation it happened inside is still
     full-screen. Kept explicit because Jorge named voice separately, and
     because a call may one day start somewhere other than here. */
  useHideTabBar(voiceActive);

  /**
   * A card committed something. Read the thread back.
   *
   * **The return path that did not exist** (T11 N38). A widget could change
   * the world and had no way to say so, so the conversation drawn around it
   * kept describing the state before the press: the draft card still offering
   * Publish for a job already published, the superseded quote still offering
   * Send. The text channel hid it because the next typed turn re-read the
   * transcript; a call has no next turn, so on voice it was four separate bug
   * reports.
   *
   * One handler, both channels, and it asks the server rather than patching
   * local state — the receipt, the supersession and the removal are all
   * decisions the server already made, and guessing at them here is how the
   * two readings drift apart again.
   */
  const reconcileThread = useCallback(() => {
    if (!threadId) return;
    loadThread(threadId)
      .then((data) => {
        // Against `live_draft`, not the transcript alone. This is the call
        // the discard bug travelled through: the card had just been thrown
        // away and the transcript still held the turn that proposed it.
        const stored = withLiveDraft(
          turnsFromTranscript(data.messages || []), data.live_draft);
        if (stored.length) setTurns(stored);
      })
      .catch(() => { /* the card has its own receipt; this is the thread's */ })
      .finally(() => { syncVoiceWidgets?.(); });
  }, [threadId, syncVoiceWidgets]);

  /**
   * What the caller is told is happening, in their language.
   *
   * A running tool names itself — "Searching FixyBees…" beats "Thinking…"
   * because it is the difference between Dany being slow and Dany being busy on
   * their behalf. Falls back to the plain state when the tool has no copy of its
   * own, which is what a tool added later will do until someone writes one.
   */
  const voiceLabel = useMemo(() => {
    if (voiceState === "thinking" && voiceTool) {
      const key = String(voiceTool).replace(/^fixydany_/, "");
      return t(`dany.voice.tool.${key}`, t("dany.voice.thinking"));
    }
    return t(`dany.voice.${voiceState}`, t("dany.voice.thinking"));
  }, [voiceState, voiceTool, t]);

  // The avatar's live state, or null once the turn is over. The hook holds the
  // closing frame for at most 3s and then settles to idle — Dany never animates
  // indefinitely after a reply.
  //
  // Voice wins over text when a call is running: `send` refuses to start a text
  // turn during a call, and the caller's mental model is the call rather than
  // the last thing they typed.
  const liveAvatar = voiceActive
    ? VOICE_AVATAR_STATE[voiceState] || "listening"
    : streaming
      ? status?.state === "tool" ? "working"
        : status?.state === "retrieving" ? "thinking"
        : "speaking"
      : null;
  const avatarState = useDanyAvatarState(liveAvatar, { settleAs: "idle" });

  useEffect(() => { refreshThreads(); }, [refreshThreads]);

  // Arriving from the hero with the microphone already pressed. `replace` wipes
  // the router state in the same tick so a back-navigation or a refresh lands on
  // a normal thread instead of dialling again.
  // Cloudflare's widget, mounted only when the backend says a challenge is
  // required AND a site key exists to render one with. Without the key the
  // backend never sends `voice_verification_required` at all — see the gate in
  // routes/voice.py — so this branch is simply never reached today. It is here
  // so that the day the keys are set, the challenge has somewhere to appear
  // instead of being an alert with nothing in it.
  const turnstileRef = useRef(null);
  const turnstileWidget = useRef(null);
  const needsChallenge = voiceError?.code === "voice_verification_required";
  useEffect(() => {
    if (!needsChallenge || !TURNSTILE_SITE_KEY || !turnstileRef.current) return;
    let cancelled = false;
    loadTurnstileScript().then(() => {
      if (cancelled || !window.turnstile || turnstileWidget.current) return;
      turnstileWidget.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        // Solving it retries the call straight away. Making the visitor press
        // the microphone again after passing a puzzle is a second ask for the
        // same intent.
        callback: (token) => { clearVoiceError(); startVoice({ turnstileToken: token }); },
      });
    }).catch(() => { /* the alert copy still stands on its own */ });
    return () => {
      cancelled = true;
      if (turnstileWidget.current && window.turnstile) {
        window.turnstile.remove(turnstileWidget.current);
        turnstileWidget.current = null;
      }
    };
  }, [needsChallenge, clearVoiceError, startVoice]);

  const voiceAutoStarted = useRef(false);
  useEffect(() => {
    if (!seededVoice || voiceAutoStarted.current) return;
    voiceAutoStarted.current = true;
    navigate(location.pathname, { replace: true, state: {} });
    startVoice();
  }, [seededVoice, startVoice, navigate, location.pathname]);

  // Route -> transcript. Reopening a conversation replays it whole, widgets
  // included: `tool_data` is stored so a reloaded thread renders exactly what
  // the live stream rendered.
  useEffect(() => {
    let alive = true;
    const settleTimers = [];
    // A thread this screen just created rewrites the URL mid-stream so the
    // conversation is linkable. That is a route change, and fetching on it
    // would read back a transcript whose assistant row is not written yet and
    // paint it over the tokens still arriving. The screen already holds the
    // authoritative state for its own thread, so it skips the round trip.
    if (routeThreadId && routeThreadId === ownThreadId.current) {
      setLoadingTranscript(false);
      return () => { alive = false; };
    }
    setThreadId(routeThreadId || null);
    if (!routeThreadId) {
      // Starting over. Forgetting the id we minted matters: without it, coming
      // back to that same conversation from the sidebar would hit the skip
      // above and render an empty screen.
      ownThreadId.current = null;
      setTurns([]);
      setOpenThread(null);
      // The failure belonged to the conversation being left. Kept, it paints a
      // blank new thread with the old thread's error card — and its Retry
      // button, still holding the old question, would fire it into a
      // conversation that never asked it. "+ New question" has to arrive
      // empty, which is the whole of what it promises.
      setError(null);
      lastAsked.current = "";
      setLoadingTranscript(false);
      return () => { alive = false; };
    }
    // Another conversation. The skip above exists for the URL rewrite this
    // screen performs on a thread it is *currently* holding; once the route
    // moves elsewhere that claim is spent, and keeping it would make a later
    // return to this same id skip the fetch and render whichever transcript was
    // loaded in between.
    ownThreadId.current = null;
    setLoadingTranscript(true);
    loadThread(routeThreadId)
      .then((data) => {
        if (!alive) return;
        // N9. The transcript is history: the draft card in it is a picture of
        // the turn that produced it, with a token that expired fifteen minutes
        // later. `live_draft` is the draft as it stands now, with one that can
        // still be spent — so a conversation you hung up on comes back with
        // the work rather than a photograph of it, and one discarded from the
        // card does not come back at all (N53). `withLiveDraft` owns both
        // halves of that; all three transcript reads on this screen go
        // through it.
        setTurns(withLiveDraft(
          turnsFromTranscript(data.messages || []), data.live_draft));
        setOpenThread(data.thread || null);
        setError(null);
        // A call whose transcript has not landed yet — a reload during one, or
        // seconds after the hangup. The tool rows are here, so the draft card
        // renders, but everything that was SAID is still with ElevenLabs and
        // arrives by webhook. Waiting for a second manual reload would be
        // asking the caller to guess when; these are the same three attempts a
        // call that ends normally already makes on its own.
        const spoken = (data.messages || []).some(
          (m) => m.role === "user" || m.role === "assistant"
        );
        if (!spoken && data.thread?.channel === "voice") {
          SETTLE_DELAYS.forEach((delay) => settleTimers.push(window.setTimeout(
            () => alive && settleVoiceTranscript(routeThreadId), delay
          )));
        }
      })
      .catch(() => {
        if (!alive) return;
        // The turns are NOT cleared. A failed fetch is a failed fetch — it is
        // not evidence that the conversation is gone, and treating it as such
        // is what made a claimed thread look deleted: after inline
        // registration the browser still held an anonymous key the server had
        // retired, that read 404'd, and this line wiped a conversation the
        // database still had in full. Show the error over what is there and
        // let the retry put it right.
        setError({ code: "thread_not_found" });
      })
      .finally(() => alive && setLoadingTranscript(false));
    return () => {
      alive = false;
      settleTimers.forEach((id) => window.clearTimeout(id));
    };
  }, [routeThreadId, settleVoiceTranscript]);

  useEffect(() => {
    anchorToBottom(true);
  }, [turns, liveTurns, status, voiceState, anchorToBottom]);

  useEffect(() => () => streamRef.current?.abort(), []);

  const send = useCallback((raw) => {
    const text = String(raw ?? "").trim();
    const photos = pendingPhotos;
    if ((!text && !photos.length) || streaming || uploadingPhotos) return;
    // Not during a call. A text turn would be written into the thread now, and
    // the voice transcript is appended when the post-call webhook lands — so
    // the typed message would end up sitting BEFORE things that were said
    // before it. The transcript's order is the one thing a conversation cannot
    // be wrong about.
    if (voiceActive) return;
    lastAsked.current = text;
    setValue("");
    setPendingPhotos([]);
    setPhotoError("");
    setError(null);
    setStreaming(true);
    // Retrieval runs before the first token, so the surface says so straight
    // away rather than showing an empty pause.
    setStatus({ state: "retrieving", label: t("dany.status.retrieving") });
    setTurns((prev) => [
      ...prev,
      { kind: "user", text, id: `local-user-${prev.length}`, photos: photos.map((p) => p.url) },
      { kind: "dany", text: "", widgets: [], id: `local-dany-${prev.length}` },
    ]);

    const patchDany = (fn) => setTurns((prev) => {
      const next = [...prev];
      for (let i = next.length - 1; i >= 0; i -= 1) {
        if (next[i].kind === "dany") {
          next[i] = fn({ ...next[i] });
          break;
        }
      }
      return next;
    });

    streamRef.current = streamChat({
      message: text,
      attachments: photos,
      threadId,
      mode: threadId ? null : seededMode,
      jobId: threadId ? null : seededJobId,
      language: threadId ? null : language,
      onEvent: (event, data) => {
        switch (event) {
          case "thread":
            setThreadId(data.thread_id);
            ownThreadId.current = data.thread_id;
            // replace: the visitor is in this conversation now, and Back should
            // return them to where they came from, not to an empty composer.
            if (!routeThreadId) navigate(`/dany/${data.thread_id}`, { replace: true, state: {} });
            break;
          case "token":
            setStatus((s) => (s?.state === "tool" ? s : { state: "generating", label: t("dany.status.generating") }));
            patchDany((turn) => ({ ...turn, text: turn.text + (data.delta || "") }));
            break;
          case "text_reset":
            // The words streamed so far described a lookup that had not run
            // yet. Dropping them is what keeps Dany from pointing at a widget
            // that is still a round away — see the orchestrator.
            patchDany((turn) => ({ ...turn, text: "" }));
            break;
          case "tool_call":
            setStatus({
              state: "tool",
              label: t(`dany.tools.${data.name}`, t("dany.status.tool")),
            });
            if (data.arguments?.zip_code) setToolZip(String(data.arguments.zip_code));
            break;
          case "tool_result":
            setStatus({ state: "result", label: t("dany.status.result") });
            // Whole-list, not patchDany: a superseding widget has to clear its
            // predecessors from EARLIER turns too, and patchDany only ever
            // touches the newest one. That is how three draft cards ended up on
            // screen at once, each with its own live Publish button.
            setTurns((prev) => {
              let index = -1;
              for (let i = prev.length - 1; i >= 0; i -= 1) {
                if (prev[i].kind === "dany") { index = i; break; }
              }
              if (index === -1) return prev;
              return addWidget(prev, index, {
                tool: data.name,
                data: data.data,
                id: `${data.name}-${index}-${prev[index].widgets.length}`,
              });
            });
            break;
          case "error":
            setError({ code: data.code, message: data.message,
                       resumeOn: data.resume_on, upgrade: data.upgrade });
            setStatus(null);
            setStreaming(false);
            break;
          case "done":
            setStatus(null);
            setStreaming(false);
            refreshThreads();
            break;
          default:
            break;
        }
      },
    });
    streamRef.current.done.then(() => {
      // A stream that ends without `done` (a hang-up, a proxy timeout) must
      // still release the UI, or the composer stays disabled forever.
      setStreaming(false);
      setStatus(null);
    });
  }, [streaming, voiceActive, threadId, seededMode, seededJobId, language, routeThreadId, navigate, refreshThreads, setStatus, t, pendingPhotos, uploadingPhotos]);

  // Filled by the voice half (F5): during a call a picked photo goes to the
  // call, not to the next typed turn.
  const attachDuringCallRef = useRef(null);
  attachDuringCallRef.current = attachToCall;

  /**
   * T19 F4. The camera beside the composer. Each photo is uploaded the moment
   * it is picked, so Send carries `file_id`s and nothing heavier; the upload's
   * own refusals (not an image, too big) are said in the reader's language.
   * During a call the same button feeds the call instead — see
   * `attachDuringCall` (F5).
   */
  const attachFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setPhotoError("");
    setUploadingPhotos(true);
    try {
      for (const file of files) {
        try {
          const photo = await uploadDanyPhoto(file, { maxMb: uploadLimitMb });
          if (voiceActive && attachDuringCallRef.current) {
            await attachDuringCallRef.current(photo);
          } else {
            setPendingPhotos((prev) => [...prev, photo].slice(0, 8));
          }
        } catch (err) {
          setPhotoError(t(`uploader.${err.code || "uploadFailed"}`, { mb: uploadLimitMb }));
          break;
        }
      }
    } finally {
      setUploadingPhotos(false);
    }
  }, [uploadLimitMb, voiceActive, t]);

  // The home hands the first message over through router state. Fire once.
  useEffect(() => {
    if (seed && !seedFired.current && !routeThreadId) {
      seedFired.current = true;
      send(seed);
    }
  }, [seed, routeThreadId, send]);

  // `returnObjects` hands back the raw array, but a missing key hands back the
  // key as a string — and `"dany.home.suggestions".slice(0, 4)` is a silent
  // "dany" rendered as four chips rather than an error.
  const suggestions = useMemo(() => {
    const list = t("dany.home.suggestions", { returnObjects: true });
    return Array.isArray(list) ? list.slice(0, 4) : [];
  }, [t]);

  /**
   * The ZIP this conversation is about.
   *
   * Read back out of the conversation rather than held as form state, because
   * that is where it actually lives: the home appends it to the first message,
   * and after that the user may correct it in any later turn. The newest
   * mention wins. A labelled mention ("ZIP 33012", "código postal 33012") is
   * preferred over a bare five-digit run so a budget or a quote total cannot be
   * mistaken for a location.
   */
  const threadZip = useMemo(() => {
    const labelled = /(?:zip|zip code|c[óo]digo postal|cp)\D{0,4}(\d{5})(?!\d)/i;
    const bare = /(?:^|[^\d$€£])(\d{5})(?!\d)/;
    for (const pattern of [labelled, bare]) {
      for (let i = turns.length - 1; i >= 0; i -= 1) {
        if (turns[i].kind !== "user") continue;
        const match = String(turns[i].text || "").match(pattern);
        if (match) return match[1];
      }
    }
    return toolZip;
  }, [turns, toolZip]);

  /**
   * A category chip inside the thread is one of Dany's actions, not a link.
   *
   * Tapping it asks Dany for FixyBees in that category, which runs
   * `find_fixybees` and renders the provider widget inline, ranked by distance.
   * Going through the conversation rather than calling the search endpoint
   * directly is the whole point: the result is persisted in the transcript, it
   * survives a reload, and there is one code path for "find FixyBees" instead
   * of two that drift.
   *
   * The category_id is deliberately NOT pasted into the message. The
   * `browse_catalog` result that produced these chips is already in the
   * model's context with its ids, so the label resolves — and a raw
   * `cat_plumbing-services` in the user's own bubble reads like a leak.
   *
   * With no ZIP anywhere in the conversation, the message says "near me" and
   * the prompt (v1.1.0, `_SURFACE`) has Dany ask for one before searching,
   * rather than returning a nationwide list ordered by nothing useful.
   */
  /** Ask the same thing again, without the wreckage of the attempt that failed. */
  const retry = useCallback(() => {
    const text = lastAsked.current;
    if (!text) return;
    setTurns((prev) => {
      const next = [...prev];
      // The failed attempt left an empty Dany turn and the user message that
      // is about to be sent again. Both go, or the retry reads as a stutter.
      if (next.length && next[next.length - 1].kind === "dany" && !next[next.length - 1].text) next.pop();
      if (next.length && next[next.length - 1].kind === "user" && next[next.length - 1].text === text) next.pop();
      return next;
    });
    send(text);
  }, [send]);

  const askForCategory = useCallback((category) => {
    const label = category?.label || "";
    if (!label) return;
    send(threadZip
      ? t("dany.widgets.askCategoryNearZip", { category: label, zip: threadZip })
      : t("dany.widgets.askCategoryNearMe", { category: label }));
  }, [send, threadZip, t]);

  // What is on screen: the stored conversation, then whatever is being said out
  // loud right now. Appended rather than merged because a voice call always
  // continues the thread — there is no case where a spoken turn belongs before
  // something already written.
  const visibleTurns = dropSuperseded(
    liveTurns.length ? [...turns, ...liveTurns] : turns
  );
  const empty = !visibleTurns.length && !loadingTranscript;
  const lastDanyIndex = visibleTurns.reduce(
    (last, turn, i) => (turn.kind === "dany" ? i : last), -1);

  /**
   * The history, with the conversation on screen always in it.
   *
   * **A refresh does not hide a live conversation.** `list_threads` filters on
   * `message_count > 0`, which counts spoken and typed turns and nothing else —
   * deliberately, or every denied microphone prompt would leave an "Untitled"
   * row in the sidebar. But a voice call has no such turn until the post-call
   * webhook writes the transcript, seconds after the hangup and on the server.
   * So a caller who reloads mid-call, or right after it, asks for their history
   * and is told they have none. Production, dthr_fa15ded173a8: five message
   * rows, four of them tool payloads from a call that really happened, and
   * `message_count` still 0.
   *
   * The list is right about what it knows. What was missing is that the screen
   * knows one more thing — which conversation it is showing — and had no way to
   * say so.
   */
  const historyThreads = useMemo(() => {
    if (!threadId || threads.some((thread) => thread.thread_id === threadId)) {
      return threads;
    }
    const known = openThread?.thread_id === threadId ? openThread : null;
    return [known || { thread_id: threadId, title: null }, ...threads];
  }, [threads, threadId, openThread]);

  const sidebar = (
    <nav
      aria-label={t("dany.thread.historyTitle")}
      data-testid="dany-history"
      style={{
        width: isDesktop ? 288 : "100%",
        flex: "0 0 auto",
        borderRight: isDesktop ? "1px solid var(--border-default)" : "none",
        borderBottom: isDesktop ? "none" : "1px solid var(--border-default)",
        background: "var(--surface-card)",
        padding: "var(--space-4)",
        // The third instance of the same bug, and the one 117df69 did not
        // reach. A thread title is `white-space: nowrap` so it can ellipsise,
        // which makes its min-content the whole line; the implicit `auto`
        // column took that as its minimum, grew to ~529px inside a 288px nav,
        // and stretched every child to match — which is why the "new thread"
        // button was the widest thing spilling into the conversation. Capping
        // the column is what lets the ellipsis do its job.
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr)",
        gap: "var(--space-2)",
        alignContent: "start",
        // Items size to their own content instead of being stretched into the
        // track. Without this a row whose title needs two lines is forced back
        // to the 44px its `min-height` asked for and its second line spills
        // over the row below — which is the same collision the timestamp used
        // to cause, one level up.
        alignItems: "start",
        maxHeight: isDesktop ? "calc(100vh - var(--navbar-height))" : "50vh",
        overflowY: "auto",
        position: isDesktop ? "sticky" : "static",
        top: "var(--navbar-height)",
      }}
    >
      <Button
        variant="secondary"
        size="sm"
        icon="plus"
        onClick={() => { setSidebarOpen(false); navigate("/dany", { state: {} }); }}
        data-testid="dany-new-thread"
      >
        {t("dany.thread.newThread")}
      </Button>
      <p style={{
        marginTop: "var(--space-2)", fontFamily: "var(--font-display)",
        fontSize: "var(--text-xs)", textTransform: "uppercase",
        letterSpacing: "var(--tracking-widest)", color: "var(--dany-meta)",
      }}>
        {t("dany.thread.historyTitle")}
      </p>
      {historyThreads.length === 0 && (
        <p style={{ fontSize: "var(--text-sm)", color: "var(--text-subtle)", lineHeight: "var(--leading-relaxed)" }}>
          {t("dany.thread.historyEmpty")}
        </p>
      )}
      {historyThreads.map((thread) => {
        const active = thread.thread_id === threadId;
        return (
          <button
            key={thread.thread_id}
            type="button"
            onClick={() => { setSidebarOpen(false); navigate(`/dany/${thread.thread_id}`, { state: {} }); }}
            data-testid={`dany-thread-${thread.thread_id}`}
            style={{
              // **Block, NOT grid, and that is the whole of the truncation
              // bug.** Grid was chosen so the timestamp would stop inheriting
              // the button's 16px strut, and it did fix that — but a grid
              // container blockifies its children, so the title's
              // `display: -webkit-box` computed to `flow-root` and
              // `-webkit-line-clamp` became inert: it only clamps a
              // `-webkit-box`. What was left was a bare `overflow: hidden`
              // cutting the text at whatever pixel the row happened to end at,
              // which is why titles broke mid-word with no ellipsis.
              //
              // Worse, the two failures compounded. With no clamp the browser
              // resolved the button's `auto` rows against its own 44px
              // `min-height` and gave the title a 10px track for an 18.2px
              // line, so the timestamp 2px below sat on top of a half-drawn
              // line of text. Measured in production before this change:
              // `grid-template-rows: 10px 10px`, title height 10px, scrollHeight
              // 36px.
              //
              // Normal flow costs nothing here: both children are already
              // `display: block` with their own `line-height`, which is what
              // actually solved the strut. `min-height` stays as a floor for
              // the touch target and no longer doubles as a ceiling.
              display: "block",
              textAlign: "left", border: "1px solid", minWidth: 0,
              borderColor: active ? "var(--hive)" : "transparent",
              background: active ? "var(--hive-a10)" : "transparent",
              borderRadius: "var(--radius-sm)", padding: "10px 12px",
              cursor: "pointer", minHeight: "var(--touch-min)",
              transition: "background-color var(--duration-base) var(--ease-out)",
            }}
          >
            {/* This wrapper is load-bearing, not tidiness. Chrome lays a
                <button>'s children out in an anonymous flex container of its
                own, and flex items are blockified — so a `-webkit-box` placed
                directly in a button computes to `flow-root` and its
                `-webkit-line-clamp` is inert no matter what the button's own
                display is. Measured, twice. The wrapper absorbs the
                blockification, and the title inside it is a normal block-level
                child of a block container, where `-webkit-box` survives. */}
            <span style={{ display: "block", minWidth: 0 }}>
            <span style={{
              display: "-webkit-box", WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
              // The gap the grid used to draw, now drawn by the element that
              // needs it.
              marginBottom: "2px",
              fontFamily: "var(--font-body)",
              fontSize: "var(--text-sm)", color: "var(--text-body)",
              lineHeight: "var(--leading-snug)",
              // Was whiteSpace: nowrap, which cut every title at one line and
              // ellipsised the half that says what the conversation is about.
              // Two lines, then ellipsis; the item grows to fit.
              overflow: "hidden", textOverflow: "ellipsis",
              overflowWrap: "anywhere",
            }}>
              {thread.title || t("dany.thread.untitled")}
            </span>
            <span style={{
              display: "block", fontFamily: "var(--font-mono)",
              fontSize: "var(--text-2xs)", color: "var(--text-subtle)",
              // Its own line-height, so the row is 11px of text in a 14px box
              // instead of borrowing the button's leading. nowrap keeps the
              // longer Spanish strings ("hace 3 minutos") on one line.
              lineHeight: "var(--leading-snug)", whiteSpace: "nowrap",
              overflow: "hidden", textOverflow: "ellipsis",
            }}>
              {formatRelative(thread.last_message_at || thread.created_at, t)}
            </span>
            </span>
          </button>
        );
      })}
    </nav>
  );

  return (
    /* The shell is exactly one viewport tall and the conversation scrolls
       inside it, which is what makes the header permanent.

       `position: sticky` on the header was already there and did nothing here:
       a sticky element can only travel inside its containing block, and the
       block was a `min-height: 100vh` column whose middle child carried
       `min-height: 0` — so the column stayed one viewport tall while its
       content overflowed and the document scrolled. Past 100vh the header had
       run out of block to stick to and left the screen. Bounding the shell and
       giving the transcript its own scroller fixes the cause; see
       `.dany-shell` in index.css for the dvh fallback.

       Below 1024px — this screen's own split, T20-M2 — the shell is also OUT
       of the document (T20-M1): fixed to the viewport and translated by
       iOS's pan, the chat's pattern. Everything
       this screen draws — the navbar, the history toggle and list, the
       transcript, the composer — stays a child of this one box, so nothing of
       the thread's own chrome can end up outside it or on top of it. */
    <div className="dany-shell" style={{ display: "flex", flexDirection: "column", background: "var(--bg-page)", overflow: "hidden" }} data-testid="dany-thread-page">
      <Navbar compact />
      <div style={{ flex: 1, display: "flex", alignItems: "stretch", minHeight: 0 }}>
        {isDesktop ? sidebar : null}
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          {!isDesktop && (
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              gap: "var(--space-2)", padding: "var(--space-3) var(--section-padding-x)",
              borderBottom: "1px solid var(--border-default)",
            }}>
              <Button
                variant="ghost" size="sm" icon="history"
                onClick={() => setSidebarOpen((v) => !v)}
                data-testid="dany-history-toggle"
              >
                {t("dany.thread.historyTitle")}
              </Button>
              <IconButton icon="plus" label={t("dany.thread.newThread")} onClick={() => navigate("/dany", { state: {} })} />
            </div>
          )}
          {!isDesktop && sidebarOpen ? sidebar : null}

          {/* T20-M2. The e-mail verification banner, as a row of this column
              above the transcript instead of the global one fixed over the
              composer. No position, no z-index: the shell's height already
              accounts for it, and `.shell-notice` lets it shrink before the
              composer ever has to. */}
          <VerifyEmailBanner placement="inline" />

          <div style={{
            flex: 1, minHeight: 0, minWidth: 0,
            overflowY: "auto", overflowX: "hidden",
            padding: "var(--space-8) var(--section-padding-x) var(--space-6)",
            display: "flex", justifyContent: "center",
          }} data-testid="dany-transcript-scroll">
            <div style={{
              width: "100%", maxWidth: "var(--dany-thread-max)", minWidth: 0,
              // Same reason as DanyProse: an `auto` column takes its minimum
              // from min-content, so one long token in one turn widened the
              // whole conversation and took every other turn past the page
              // padding with it.
              display: "grid", gridTemplateColumns: "minmax(0, 1fr)",
              gap: "var(--dany-gap-turn)", alignContent: "start",
            }}>
              {loadingTranscript && (
                <div style={{ display: "grid", gap: "var(--space-3)" }} data-testid="dany-transcript-loading">
                  <Skeleton height={18} width="45%" />
                  <Skeleton height={14} />
                  <Skeleton height={14} width="80%" />
                </div>
              )}

              {empty && (
                <div data-testid="dany-thread-empty">
                  <DanyTurn text={t("dany.thread.greeting")} />
                  <div style={{ marginTop: "var(--space-6)" }}>
                    <SuggestionChips
                      label={t("dany.home.tryAsking")}
                      items={suggestions}
                      onPick={(s) => send(s.label)}
                    />
                  </div>
                </div>
              )}

              {visibleTurns.map((turn, index) => {
                if (turn.kind === "user") {
                  // T19 F4: the photo is part of what they said, so it sits
                  // with their turn, on their side of the conversation.
                  const photos = turn.photos || [];
                  if (!photos.length) {
                    return (
                      <DanyTurn key={turn.id} from="user" text={turn.text}
                        userName={user?.full_name || t("dany.thread.you")} />
                    );
                  }
                  return (
                    <div key={turn.id} style={{ display: "grid", gap: "var(--space-2)" }}>
                      <DanyPhotoStrip urls={photos} align="end" size={96}
                        testid={`dany-turn-photos-${turn.id}`} />
                      {turn.text && (
                        <DanyTurn from="user" text={turn.text}
                          userName={user?.full_name || t("dany.thread.you")} />
                      )}
                    </div>
                  );
                }
                // Exactly one Dany on screen, always. The face lives on the last
                // Dany turn and nowhere else, and the streaming indicator lives
                // INSIDE that turn with its own avatar suppressed — so "thinking"
                // and "writing" animate one bee, not two side by side. Earlier
                // turns keep the gutter but not the portrait.
                const live = index === lastDanyIndex;
                const showsPlans = turn.widgets.some((w) => w.tool === "get_plans");
                return (
                  <DanyTurn
                    key={turn.id}
                    text={showsPlans
                      ? trimRecitedPlans(turn.text, t("dany.widgets.plansIntro"))
                      : turn.text}
                    showAvatar={live}
                    avatarState={live ? avatarState : "idle"}
                    steps={!turn.widgets.some((w) => SET_TOOLS.has(w.tool))}
                  >
                    {turn.widgets.length > 0 && (
                      <div style={{
                        display: "grid", gridTemplateColumns: "minmax(0, 1fr)",
                        gap: "var(--space-5)", minWidth: 0,
                      }}>
                        {/* One boundary per widget, and that granularity is
                            the point (N35). A card that throws costs its own
                            rectangle and nothing else: the sentence above it,
                            the turns around it, the history and the composer
                            all stay. Before this, one `ReferenceError` inside
                            a provider card unmounted the React root and left a
                            blank page on the same URL. `resetKey` is the
                            widget's id, so a superseded card does not inherit
                            its predecessor's failure. */}
                        {turn.widgets.map((w) => (
                          <ErrorBoundary
                            key={w.id}
                            label={w.tool}
                            resetKey={w.id}
                            title={t("dany.errors.widgetTitle")}
                          >
                            <DanyWidget
                              tool={w.tool}
                              data={w.data}
                              onAskCategory={askForCategory}
                              // The write widgets need it to replay the anonymous
                              // key on the execute call (DY2.1): a visitor with no
                              // session is identified by the key of the thread the
                              // proposal came from, and nothing else.
                              threadId={threadId}
                              // And the way back (T11 N38). A card that commits
                              // says so, and the thread re-reads itself.
                              onCommitted={reconcileThread}
                            />
                          </ErrorBoundary>
                        ))}
                      </div>
                    )}
                    {live && status && (
                      <DanyStatus
                        state={status.state}
                        label={status.label}
                        detail={status.detail}
                        showAvatar={false}
                      />
                    )}
                  </DanyTurn>
                );
              })}

              {/* A turn that failed before any Dany turn existed (a rejected
                  thread, a dead network) has nowhere to hang the indicator. */}
              {status && lastDanyIndex === -1 && (
                <DanyStatus state={status.state} label={status.label} detail={status.detail} />
              )}

              {error && (
                <Alert tone="danger" title={t("dany.errors.title")}>
                  {error.code === "dany_paused"
                    // DY1.7 M4.3: the month's ceiling stopped Dany, and this
                    // says until when — plus "a plan with more" for a FixyBee
                    // whose own allowance is spent, never buying quotes. The
                    // "temporarily unavailable" line stands in only if a
                    // language lacks the key.
                    ? t(error.upgrade ? "dany.errors.dany_paused_upgrade"
                                      : "dany.errors.dany_paused", {
                        date: formatDate(error.resumeOn),
                        defaultValue: t("dany.errors.AIBudgetExceeded"),
                      })
                    : t(`dany.errors.${error.code}`, t("dany.errors.generic"))}
                  {/* The code, verbatim and selectable.
                      Daniel's report of this dialog carried only the title,
                      which every failure shares, so the one turn that broke in
                      production could not be told apart from the four other
                      things it might have been — and the failure path leaves
                      nothing in the database to look up afterwards. A person
                      photographing this screen should be handing us the
                      diagnosis. */}
                  <span style={{
                    display: "block", marginTop: "var(--space-2)",
                    fontFamily: "var(--font-mono)", fontSize: "var(--text-2xs)",
                    color: "var(--text-muted)", userSelect: "all",
                  }} data-testid="dany-error-code">
                    {error.code}{threadId ? ` · ${threadId}` : ""}
                  </span>
                  {lastAsked.current && !streaming && (
                    <span style={{ display: "block", marginTop: "var(--space-3)" }}>
                      <Button variant="secondary" size="sm" icon="rotate-ccw"
                        onClick={retry} data-testid="dany-retry">
                        {t("dany.errors.retry")}
                      </Button>
                    </span>
                  )}
                </Alert>
              )}

              <div ref={bottomRef} />
            </div>
          </div>

          {/* No longer sticky: the transcript scrolls in its own box above,
              so the composer is simply the last row of a bounded column and
              nothing passes underneath it.

              The bottom padding is NOT written here (T20-M1). It carried the
              home indicator's inset inline, keyboard up or down, and an inline
              `padding` outranks the stylesheet's keyboard-aware rule — so it is
              `.dany-shell .composer-safe` in index.css that owns it, and the
              three other sides stay inline. */}
          <div className="composer-safe" style={{
            flex: "0 0 auto",
            paddingTop: "var(--space-4)",
            paddingInline: "var(--section-padding-x)",
            background: "var(--canvas)", borderTop: "1px solid var(--border-default)",
            display: "flex", justifyContent: "center",
          }}>
            <div style={{ width: "100%", maxWidth: "var(--dany-thread-max)" }}>
              {/* The call's own state lives above the composer, not inside the
                  transcript: it is the state of a control, not something Dany
                  said, and putting it in the conversation would leave a
                  "Connecting…" line in the history forever. */}
              {voiceActive && (
                <VoicePresence
                  state={voiceState}
                  tool={voiceTool}
                  countdownMs={voiceEndingMs}
                  label={voiceLabel}
                />
              )}

              {voiceError && (
                <div style={{ marginBottom: "var(--space-3)" }}>
                  {/* The end of a free preview is not a failure, and dressing it
                      in the danger tone would teach the visitor that they did
                      something wrong at the exact moment we want them to sign
                      up. Same component, different tone, and a real CTA next to
                      the dismissal. */}
                  <Alert
                    tone={voiceError.code === "voice_preview_over" ? "info" : "danger"}
                    title={voiceError.code === "voice_preview_over"
                      ? t("dany.voice.start") : t("dany.voice.errorTitle")}
                  >
                    {t(`dany.voice.${voiceError.code}`, t("dany.voice.voice_error"))}
                    {needsChallenge && TURNSTILE_SITE_KEY && (
                      <div style={{ marginTop: "var(--space-3)" }}
                        ref={turnstileRef} data-testid="dany-voice-turnstile" />
                    )}
                    <span style={{
                      display: "flex", gap: "var(--space-2)", flexWrap: "wrap",
                      marginTop: "var(--space-3)",
                    }}>
                      {voiceError.code === "voice_preview_over" && (
                        <Button variant="primary" size="sm"
                          data-testid="dany-voice-signup"
                          onClick={() => navigate(withReturnTo(
                            "/signup/client", pathOf(location)))}>
                          {t("dany.voice.createAccount")}
                        </Button>
                      )}
                      <Button variant="secondary" size="sm"
                        onClick={clearVoiceError} data-testid="dany-voice-dismiss">
                        {t("dany.voice.dismiss")}
                      </Button>
                    </span>
                  </Alert>
                </div>
              )}

              {/* T19 F4. What is waiting to go out with the next turn, and what
                  went wrong picking it. Above the composer and not inside it:
                  the box is one row on a phone, and a thumbnail inside it is
                  the thing that would push the field off its line. */}
              {(pendingPhotos.length > 0 || photoError) && (
                <div style={{ marginBottom: "var(--space-3)", display: "grid", gap: "var(--space-2)" }}>
                  <DanyPhotoStrip
                    urls={pendingPhotos.map((p) => p.url)}
                    size={64}
                    busy={uploadingPhotos}
                    onRemove={(i) => setPendingPhotos((prev) => prev.filter((_, j) => j !== i))}
                    testid="dany-pending-photos"
                  />
                  {photoError && (
                    <p style={{ fontSize: "var(--text-sm)", color: "var(--danger)" }}
                       data-testid="dany-photo-error">{photoError}</p>
                  )}
                </div>
              )}

              <DanyComposer
                size="inline"
                value={value}
                onChange={setValue}
                onSubmit={() => send(value)}
                placeholder={t("dany.home.placeholder")}
                placeholderShort={t("dany.home.placeholderShort")}
                attachments
                onAttach={attachFiles}
                attachBusy={uploadingPhotos}
                canSend={pendingPhotos.length > 0 && !uploadingPhotos}
                /* X5: a photo needs an account. A visitor's tap goes to sign
                   up and comes back here — the button says why before the tap,
                   and is never `disabled`, because a disabled button explains
                   itself only on hover. */
                attachBlocked={!user}
                attachTitle={user ? t("dany.composer.attach") : t("dany.composer.attachSignIn")}
                onAttachBlocked={() => navigate(withReturnTo(
                  "/signup/client", pathOf(location)))}
                listening={voiceActive}
                /* **Anonymous callers may speak since 2026-08-17 (Daniel).**
                   `!user` used to disable this, which made the mic render
                   struck-through for every signed-out visitor with the reason
                   living only in a `title` — invisible on touch, where there is
                   no hover. The only thing that closes the button now is a text
                   turn already streaming, which is a race rather than a
                   permission. Anything else that refuses a call refuses it in
                   the open, with copy, through `voiceError`. */
                voiceDisabled={streaming}
                voiceTitle={
                  voiceActive ? t("dany.voice.stop") : t("dany.voice.start")
                }
                onVoice={voiceActive ? stopVoice : startVoice}
              />
              {/* Screen readers get the streaming state as text; the four visual
                  treatments carry it for everyone else. A call announces itself
                  through the same channel, because none of its four states have
                  any text of their own. */}
              <span aria-live="polite" style={srOnly}>
                {voiceActive ? t(`dany.voice.${voiceState}`)
                  : streaming ? status?.label || "" : ""}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const srOnly = {
  position: "absolute", width: 1, height: 1, padding: 0, margin: -1,
  overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap", border: 0,
};

export { turnsFromTranscript };
